import NitroService from "../../../utils/nitro.service";
import { SelectedOrigin } from "../../../pages/home/favoriteOrigin/AddOriginColumn";
import {
  AddMissingProductModalState,
  Quote,
} from "../../../types/IMultiLocationQuoteMobile";
import {
  IFavoriteOriginMonth,
  IFavoriteOriginPricesResponse,
  IFavoriteOriginWithMonths,
} from "../../../types/IFavoriteOrigins";
import {
  handleAddMissingProductWithState,
  submitAddMissingProductForSelectedOccupantsWithState,
  getLocationDetailsWithRetry as getLocationDetailsWithRetryUtil,
} from "../multiLocQuoteDetailsPanel/multiLocQuoteDetailsUtils";

type OriginType = "koch" | "competitor";

export const getMonthKey = (
  month: IFavoriteOriginMonth["month"] | string,
): string => {
  if (typeof month === "string") {
    return month.slice(0, 7);
  }
  return `${month.year}-${String(month.month).padStart(2, "0")}`;
};

export const sortByCreateDate = (
  a: IFavoriteOriginWithMonths,
  b: IFavoriteOriginWithMonths,
): number => {
  const dateA = (a as any).create_date;
  const dateB = (b as any).create_date;
  if (!dateA && !dateB) return 0;
  if (!dateA) return 1;
  if (!dateB) return -1;

  if (dateA.year !== dateB.year) return dateB.year - dateA.year;
  if (dateA.month !== dateB.month) return dateB.month - dateA.month;
  if (dateA.day !== dateB.day) return dateB.day - dateA.day;
  if (dateA.hour !== dateB.hour) return dateB.hour - dateA.hour;
  if (dateA.minute !== dateB.minute) return dateB.minute - dateA.minute;
  return dateB.second - dateA.second;
};

export const toNumberOrNull = (value: string): number | null => {
  const trimmed = (value || "").trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

export const getMonthRange = (
  monthKey: string,
): { startDate: string; endDate: string } => {
  const [yearStr, monthStr] = monthKey.split("-");
  const year = Number(yearStr);
  const month = Number(monthStr);

  if (!Number.isFinite(year) || !Number.isFinite(month)) {
    const now = new Date();
    const fallbackYear = now.getFullYear();
    const fallbackMonth = now.getMonth() + 1;
    const startDate = `${fallbackYear}-${String(fallbackMonth).padStart(
      2,
      "0",
    )}-01`;
    const endDateDate = new Date(fallbackYear, fallbackMonth, 0);
    const endDate = `${fallbackYear}-${String(fallbackMonth).padStart(
      2,
      "0",
    )}-${String(endDateDate.getDate()).padStart(2, "0")}`;
    return { startDate, endDate };
  }

  const startDate = `${year}-${String(month).padStart(2, "0")}-01`;
  const endDateDate = new Date(year, month, 0);
  const endDate = `${year}-${String(month).padStart(2, "0")}-${String(
    endDateDate.getDate(),
  ).padStart(2, "0")}`;

  return { startDate, endDate };
};

export const getMonthForFavorite = (
  favorite: IFavoriteOriginWithMonths,
  monthKey: string,
): IFavoriteOriginMonth | null => {
  if (!favorite.months || favorite.months.length === 0) return null;
  return favorite.months.find((m) => getMonthKey(m.month) === monthKey) || null;
};

export const normalizeFavoriteOriginPrices = (
  raw: any[],
): IFavoriteOriginPricesResponse[] => {
  const normalizePrices = (prices: any) => {
    if (!prices) return prices;
    return {
      posted: prices.posted ?? null,
      truck: prices.truck ?? null,
      rail: prices.rail ?? null,
    };
  };

  return raw.map((productEntry) => ({
    ...productEntry,
    favorites: (productEntry.favorites || []).map((fav: any) => ({
      ...fav,
      months: (fav.months || []).map((month: any) => ({
        ...month,
        isSplit:
          typeof month?.isSplit === "boolean"
            ? month.isSplit
            : (month?.periods || []).length > 1,
        periods: (month.periods || []).map((period: any) => ({
          ...period,
          prices: normalizePrices(period.prices),
        })),
      })),
    })),
  }));
};

export const normalizeFavoriteMonthPrices = (
  month: IFavoriteOriginMonth,
): IFavoriteOriginMonth => ({
  ...month,
  periods: month.periods.map((period) => ({
    ...period,
    prices: {
      posted: period.prices.posted
        ? {
            ...period.prices.posted,
            price: toNumberOrNull(String(period.prices.posted.price ?? "")),
          }
        : null,
      truck: period.prices.truck
        ? {
            ...period.prices.truck,
            price: toNumberOrNull(String(period.prices.truck.price ?? "")),
          }
        : null,
      rail: period.prices.rail
        ? {
            ...period.prices.rail,
            price: toNumberOrNull(String(period.prices.rail.price ?? "")),
          }
        : null,
    },
  })),
});

export const getLocationDetailsWithRetry = (
  locationId: string,
  maxAttempts = 3,
): Promise<any> =>
  getLocationDetailsWithRetryUtil({
    locationId,
    maxAttempts,
    fetchLocationDetails: (id) => NitroService.getLocationDetails(id),
  });

export const applyAddMissingProductForOrigin = async ({
  quote,
  originId,
  missingProductName,
  selectedOccupants,
  existingLocationDetails,
  failedNotification,
  addOriginLocallyFn,
}: {
  quote: Quote;
  originId: string;
  addProductKey: string;
  missingProductName: string;
  selectedOccupants: any[];
  existingLocationDetails?: any;
  failedNotification: (title: string, message: string) => void;
  addOriginLocallyFn: (
    origin: { id: string; name: string },
    type: "koch" | "competitor",
  ) => void;
}): Promise<void> => {
  const normalizedProductName = (missingProductName || "").toUpperCase();

  const currentLocationDetails =
    existingLocationDetails || (await getLocationDetailsWithRetry(originId));
  const currentOccupants = Array.isArray(currentLocationDetails?.occupants)
    ? currentLocationDetails.occupants
    : [];

  if (!currentOccupants.length) {
    failedNotification(
      "Failed to add missing product.",
      "No occupants found for this origin.",
    );
    return;
  }

  const selectedIds = new Set(
    selectedOccupants.map((o: any) => o?.id || o?.name),
  );

  const occupantsToUpdate = currentOccupants.map((occupant: any) => {
    if (!selectedIds.has(occupant?.id || occupant?.name)) return occupant;
    const occupantProducts = Array.isArray(occupant?.products)
      ? occupant.products
      : [];
    const hasProduct = occupantProducts.some(
      (p: any) =>
        typeof p?.product === "string" &&
        p.product.toUpperCase() === normalizedProductName,
    );
    if (hasProduct) return occupant;
    return {
      ...occupant,
      products: [
        ...occupantProducts,
        { product: normalizedProductName, quantity: 0 },
      ],
    };
  });

  await NitroService.updateOccupants({
    id: originId,
    occupants: occupantsToUpdate,
  });
  addOriginLocallyFn(
    { id: quote.origin.id || originId, name: quote.origin.name || "" },
    quote.competitor ? "competitor" : "koch",
  );
};

export const submitAddMissingProductForOrigin = async ({
  addMissingProductModalState,
  selectedOccupantKeys,
  addMissingProductInFlight,
  addMissingProductCooldownUntil,
  setSubmittingAddMissingProduct,
  setAddMissingProductModalState,
  setSelectedOccupantKeys,
  failedNotification,
  addOriginLocallyFn,
}: {
  addMissingProductModalState: AddMissingProductModalState | null;
  selectedOccupantKeys: string[];
  addMissingProductInFlight: { current: boolean };
  addMissingProductCooldownUntil: { current: number };
  setSubmittingAddMissingProduct: (value: boolean) => void;
  setAddMissingProductModalState: (
    value: AddMissingProductModalState | null,
  ) => void;
  setSelectedOccupantKeys: (value: string[]) => void;
  failedNotification: (title: string, message: string) => void;
  addOriginLocallyFn: (
    origin: { id: string; name: string },
    type: "koch" | "competitor",
  ) => void;
}): Promise<void> => {
  await submitAddMissingProductForSelectedOccupantsWithState({
    addMissingProductModalState,
    selectedOccupantKeys,
    addMissingProductInFlight,
    addMissingProductCooldownUntil,
    setSubmittingAddMissingProduct,
    setAddMissingProductModalState,
    setSelectedOccupantKeys,
    applyAddMissingProductFn: (params) =>
      applyAddMissingProductForOrigin({
        ...params,
        failedNotification,
        addOriginLocallyFn,
      }),
  });
};

export const handleAddMissingProductForOrigin = ({
  pendingOrigin,
  addMissingProductInFlight,
  addMissingProductCooldownUntil,
  selectedProduct,
  setShowProductNotAvailablePanel,
  setAddMissingProductModalState,
  setSelectedOccupantKeys,
  failedNotification,
  addOriginLocallyFn,
}: {
  pendingOrigin: { origin: SelectedOrigin; type: OriginType } | null;
  addMissingProductInFlight: { current: boolean };
  addMissingProductCooldownUntil: { current: number };
  selectedProduct: string;
  setShowProductNotAvailablePanel: (value: boolean) => void;
  setAddMissingProductModalState: (
    value: AddMissingProductModalState | null,
  ) => void;
  setSelectedOccupantKeys: (value: string[]) => void;
  failedNotification: (title: string, message: string) => void;
  addOriginLocallyFn: (
    origin: { id: string; name: string },
    type: "koch" | "competitor",
  ) => void;
}): void => {
  if (!pendingOrigin) return;
  const { origin, type } = pendingOrigin;
  const quote: Quote = {
    origin: { id: origin.id, name: origin.name },
    competitor: type === "competitor",
    mode: "TRUCK",
    minPrice: null,
    maxPrice: null,
  };
  setShowProductNotAvailablePanel(false);
  handleAddMissingProductWithState({
    quote,
    addMissingProductInFlight,
    addMissingProductCooldownUntil,
    selectedProduct,
    fallbackProduct: selectedProduct,
    getLocationDetailsWithRetryFn: (locationId) =>
      getLocationDetailsWithRetry(locationId),
    setAddMissingProductModalState,
    setSelectedOccupantKeys,
    failedNotification,
    applyAddMissingProductFn: (params) =>
      applyAddMissingProductForOrigin({
        ...params,
        failedNotification,
        addOriginLocallyFn,
      }),
  });
};
